import { DownloadCloud } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";

function formatDateTime(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

export const BackfillPanel = () => {
  const [progress, setProgress] = useState(null);
  const [launching, setLaunching] = useState(false);

  const loadProgress = async () => {
    try {
      const { data } = await api.get("/treatments/backfill-progress");
      setProgress(data.batch);
    } catch {
      /* silencieux */
    }
  };

  useEffect(() => {
    loadProgress();
  }, []);

  const running = progress && progress.status === "en_cours";

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(loadProgress, 2000);
    return () => clearInterval(timer);
  }, [running]);

  const launch = async () => {
    setLaunching(true);
    try {
      const { data } = await api.post("/treatments/backfill-enrichment");
      toast.success(data.message);
      await loadProgress();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Le rattrapage n’a pas pu démarrer.");
    } finally {
      setLaunching(false);
    }
  };

  return (
    <section className="bulk-retry-panel" data-testid="backfill-panel">
      <div className="bulk-retry-head">
        <div>
          <p className="eyebrow">RATTRAPAGE ENRICHISSEMENT</p>
          <p className="bulk-retry-hint" data-testid="backfill-hint">
            Des dossiers importés ou déplacés en masse dans « Dossiers à enrichir » ne déclenchent pas le webhook noCRM et restent bloqués. Ce rattrapage va chercher tous les dossiers présents dans cette étape et les enrichit un par un (calcul, devis généré et envoyé automatiquement), avec une courte pause entre chaque pour ménager noCRM.
          </p>
        </div>
        <button
          type="button"
          className="secondary-button"
          onClick={launch}
          disabled={launching || running}
          data-testid="backfill-button"
        >
          <DownloadCloud size={16} />
          {launching ? "Lancement…" : running ? "Rattrapage en cours…" : "Enrichir les dossiers en attente"}
        </button>
      </div>
      {progress && progress.status === "en_cours" && (
        <div className="retry-progress" data-testid="backfill-progress-banner">
          <div className="retry-progress-track"><div className="retry-progress-fill" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} /></div>
          <p data-testid="backfill-progress-text">
            Rattrapage en cours : <strong>{progress.done}/{progress.total}</strong> traités — {progress.success} réussi{progress.success > 1 ? "s" : ""}, {progress.failed} échec{progress.failed > 1 ? "s" : ""} · mise à jour automatique
          </p>
        </div>
      )}
      {progress && progress.status === "termine" && Date.now() - new Date(progress.finished_at).getTime() < 15 * 60 * 1000 && (
        <p className="retry-progress-done" data-testid="backfill-progress-done">
          Dernier rattrapage terminé ({formatDateTime(progress.finished_at)}) : {progress.success} réussi{progress.success > 1 ? "s" : ""} sur {progress.total}, {progress.failed} échec{progress.failed > 1 ? "s" : ""}.
        </p>
      )}
      {progress && progress.status === "interrompu" && (
        <p className="review-alert" data-testid="backfill-progress-interrupted">
          Le dernier rattrapage a été interrompu à {progress.done}/{progress.total}. Relancez-le pour traiter les dossiers restants.
        </p>
      )}
    </section>
  );
};
